import type { CSSProperties, FC, ReactNode } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ActiveAvatarHoverDropdown } from '@/shared/components/dropdowns';
import EnrichedPill from '@/shared/components/enriched-pills/EnrichedPill';
import { EnrichedWorkspaceResourcePopover } from '@/shared/components/popovers';
import type { CommentMentionTarget, ResourceActivityResourceType } from '@/types';

import CommentMentionIdentity from '../CommentMentionIdentity';

import './index.css';

interface CommentMentionTagProps {
    hoverable?: boolean;
    interactive?: boolean;
    mention: CommentMentionTarget;
    popupZIndex?: number;
    publicMemoId?: string;
    publicWorkspaceMetadata?: boolean;
    style?: CSSProperties;
}

const memberHoverCollaborator = (mention: CommentMentionTarget) => ({
    member: {
        id: mention.id,
        permission: 'Member',
        user: {
            avatar: mention.avatar,
            emailAddress: mention.handle,
            firstName: mention.label,
            id: mention.userId,
        },
    },
});

const CommentMentionTag: FC<CommentMentionTagProps> = ({
    hoverable = true,
    interactive = true,
    mention,
    popupZIndex,
    publicMemoId,
    publicWorkspaceMetadata,
    style,
}) => {
    const { token } = useAntDesignTheme();
    const alignMention = (content: ReactNode) => (
        <span
            className='workspace-mention-layout'
            data-workspace-mention-layout='true'
            style={{
                height: token.controlHeightSM,
            }}
        >
            {content}
        </span>
    );
    const content = <CommentMentionIdentity avatarSize={16} labelStyle={{ maxWidth: '12rem' }} mention={mention} />;
    const trigger: ReactNode = (
        <EnrichedPill href={mention.href} interactive={interactive} style={style} workspaceMention>
            {content}
        </EnrichedPill>
    );

    if (!hoverable) {
        return alignMention(trigger);
    }

    if (mention.kind === 'member') {
        if (publicMemoId) {
            return alignMention(trigger);
        }

        return alignMention(
            <ActiveAvatarHoverDropdown
                collaborator={memberHoverCollaborator(mention)}
                overlayZIndex={popupZIndex}
                showResourceContext={false}
                tooltipPlacement='bottomLeft'
            >
                {trigger}
            </ActiveAvatarHoverDropdown>,
        );
    }

    const resourceType = (
        mention.kind === 'team' ? 'teams' : (mention.resourceType ?? 'resources')
    ) as ResourceActivityResourceType;

    return alignMention(
        <EnrichedWorkspaceResourcePopover
            placement='bottomLeft'
            publicMemoId={publicMemoId}
            publicWorkspaceMetadata={publicWorkspaceMetadata}
            reference={{
                href: mention.href,
                id: mention.id,
                label: mention.label,
                listType: mention.listType,
                resourceType,
                updatedAt: mention.updatedAt,
                website: mention.website,
            }}
            zIndex={popupZIndex}
        >
            {trigger}
        </EnrichedWorkspaceResourcePopover>,
    );
};

export default CommentMentionTag;
